/**
 * File block (spec §7.4.10, §8.14): any upload, shown as a card — never rendered in the page.
 * Opening is a top-level navigation: `download` saves the file under its original name, `inline`
 * opens `?disposition=inline` (served with `Content-Security-Policy: sandbox`, §19.3) in a new tab
 * or a popup window. The author's `disposition`/`openIn` sit in the block toolbar because they change
 * what every reader gets.
 */
import type { AssetRef } from "@kss/schema";
import { File, FileArchive, FileAudio, FileCode, FileImage, FileSpreadsheet, FileText, FileVideo, type LucideIcon } from "lucide-react";
import { assetOpenUrl, fileIconKind, formatBytes, type Disposition, type IconKind, type OpenIn } from "./model";
import { useLoroSelector } from "../../sync/hooks";
import { MediaShell } from "../media/MediaShell";
import { useAsset } from "../media/useAsset";
import { useT } from "../../i18n";
import type { BlockComponentProps } from "../registry";
import { bodyMap } from "../textField";

const ICONS: Record<IconKind, LucideIcon> = {
  image: FileImage,
  audio: FileAudio,
  video: FileVideo,
  text: FileText,
  archive: FileArchive,
  spreadsheet: FileSpreadsheet,
  code: FileCode,
  file: File,
};

function FileIcon({ mime }: { mime: string }) {
  const Icon = ICONS[fileIconKind(mime)];
  return <Icon size={28} className="shrink-0 text-neutral-500" data-kind={fileIconKind(mime)} />;
}


export function FileBlock({ handle, treeId, node, readOnly }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const state = useLoroSelector(
    handle.doc,
    () => ({
      asset: (body?.get("asset") as AssetRef | null) ?? null,
      disposition: (body?.get("disposition") === "inline" ? "inline" : "download") as Disposition,
      openIn: (body?.get("openIn") === "window" ? "window" : "tab") as OpenIn,
    }),
    (a, b) => a.asset?.hash === b.asset?.hash && a.disposition === b.disposition && a.openIn === b.openIn,
  );
  const asset = state?.asset ?? null;
  const { upload, stored, localUrl } = useAsset(asset?.hash ?? null);
  if (!body || !state) return null;

  const set = (key: "disposition" | "openIn", value: string) => {
    body.set(key, value);
    handle.doc.commit();
  };
  const open = () => {
    if (!asset || !stored) return;
    const url = assetOpenUrl(asset.hash, "inline");
    // noopener: the opened document gets no handle on this window (it is sandboxed anyway)
    const features = state.openIn === "window" ? "popup=yes,width=960,height=720,noopener" : "noopener";
    window.open(url, "_blank", features);
  };
  // before the upload is stored, the local OPFS copy is what can be saved (§8.14 point 5)
  const downloadHref = stored && asset ? assetOpenUrl(asset.hash, "download") : localUrl;

  return (
    <MediaShell
      handle={handle}
      treeId={treeId}
      body={body}
      accept="*/*"
      readOnly={readOnly}
      asset={asset}
      upload={upload}
      extraFields={
        !readOnly && asset ? (
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-neutral-500" data-testid="file-controls">
            <label className="flex items-center gap-1">
              {t("file.whenOpened")}
              <select className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={state.disposition} data-testid="file-disposition" onChange={(e) => set("disposition", e.target.value)}>
                <option value="download">{t("file.download")}</option>
                <option value="inline">{t("file.inline")}</option>
              </select>
            </label>
            {state.disposition === "inline" && (
              <label className="flex items-center gap-1">
                {t("file.inA")}
                <select className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={state.openIn} data-testid="file-open-in" onChange={(e) => set("openIn", e.target.value)}>
                  <option value="tab">{t("file.newTab")}</option>
                  <option value="window">{t("file.newWindow")}</option>
                </select>
              </label>
            )}
          </div>
        ) : null
      }
    >
      {asset && (
        <div className="flex items-center gap-3" data-testid="file-card" data-disposition={state.disposition} data-open-in={state.openIn}>
          <FileIcon mime={asset.mime} />
          <div className="min-w-0 grow">
            <div className="truncate text-sm font-medium" data-testid="file-name" title={asset.name}>
              {asset.name || t("file.name")}
            </div>
            <div className="text-xs text-neutral-500" data-testid="file-size">
              {formatBytes(asset.size)} · {asset.mime || t("file.unknownType")}
            </div>
          </div>
          {state.disposition === "inline" ? (
            <button className="rounded border border-neutral-300 px-2 py-0.5 text-xs disabled:opacity-50 dark:border-neutral-700" disabled={!stored} data-testid="file-open" onClick={open}>
              {t("file.open")}
            </button>
          ) : downloadHref ? (
            <a className="rounded border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700" href={downloadHref} download={asset.name || true} data-testid="file-download">
              {t("file.downloadButton")}
            </a>
          ) : (
            <span className="text-xs text-neutral-500">{t("file.offline")}</span>
          )}
        </div>
      )}
    </MediaShell>
  );
}
